import { useEffect, useState } from "react";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { useToast } from "@/src/components/ui/use-toast";
import { fetch } from "@/src/lib/fetch";

/**
 * One compose dialog for both reminders (v14): crew list (chase round 1–3,
 * copy varies by round and party) and payment (live outstanding amount).
 * Subject and body are editable; "send me a copy first" previews to the
 * sender. Outside production every send is redirected to the sender anyway.
 */
type Template = "payment_reminder" | "crew_list_reminder" | "licence_reminder" | "vhf_reminder";
const TITLE: Record<Template, string> = { payment_reminder: "Payment reminder", crew_list_reminder: "Crew list reminder", licence_reminder: "Skipper licence reminder", vhf_reminder: "VHF certificate reminder" };
type Preview = {
  template: Template;
  canSend: boolean;
  reason: string | null;
  preview: { recipient: { email: string; name?: string } | null; subject: string; editable: { greeting?: string; intro?: string; body?: string; signoff?: string } } | null;
  round?: number;
  chasesSent?: number;
  party?: string;
  outstanding?: { amount: number; currency: string | null; dueDate: string | null; invoiceNumber: string | null };
  redirected?: boolean;
};

export function ReminderModal({ open, onClose, bookingId, template }: { open: boolean; onClose: () => void; bookingId: string; template: Template }) {
  const { toast } = useToast();
  const [p, setP] = useState<Preview | null>(null);
  const [round, setRound] = useState<number>(1);
  const [subject, setSubject] = useState("");
  const [intro, setIntro] = useState("");
  const [body, setBody] = useState("");
  const [copyFirst, setCopyFirst] = useState(true);
  const [busy, setBusy] = useState(false);
  const isPay = template === "payment_reminder";

  const load = (r?: number) => {
    const qs = r ? `?round=${r}` : "";
    fetch<{ data: Preview }>(`/api/bookings/${bookingId}/reminders/${template}/preview${qs}`)
      .then((res: any) => {
        const d: Preview = res.data;
        setP(d);
        if (d.round) setRound(d.round);
        setSubject(d.preview?.subject ?? "");
        setIntro(d.preview?.editable?.intro ?? "");
        setBody(d.preview?.editable?.body ?? "");
      })
      .catch((e: any) => setP({ template, canSend: false, reason: e?.message ?? "Couldn't load the preview.", preview: null }));
  };
  useEffect(() => { if (open) load(); }, [open, bookingId, template]);

  const send = async (sendToSelf: boolean) => {
    setBusy(true);
    try {
      const r: any = await fetch(`/api/bookings/${bookingId}/reminders/${template}`, "POST", {
        round: isPay ? undefined : round,
        // Empty fields mean "use the default", never a deliberate blank.
        editable: { ...(intro.trim() ? { intro } : {}), ...(body.trim() ? { body } : {}) },
        subjectOverride: subject,
        sendToSelf,
      });
      const d = r.data;
      toast({
        title: d.preview || d.redirected ? "Test copy sent to you" : "Reminder sent",
        description: d.preview || d.redirected ? `Sent to ${d.recipient} — outside production nothing goes to the client.` : `Sent to ${d.recipient}.${d.round ? ` Chase round ${d.round} recorded.` : ""}`,
      });
      if (!sendToSelf) onClose();
    } catch (e: any) {
      toast({ title: "Couldn't send", description: e?.message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const money = (n: number, c?: string | null) => new Intl.NumberFormat("en-EN", { style: "currency", currency: c || "EUR", maximumFractionDigits: 0 }).format(n);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[640px] max-h-[calc(100%-60px)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">{TITLE[template]}</DialogTitle>
        </DialogHeader>
        {!p && <p className="text-sm text-slate-500">Loading preview…</p>}
        {p && !p.canSend && <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">{p.reason}</p>}
        {p && p.preview && (
          <div className="flex flex-col gap-3 text-sm">
            <div className="rounded-md bg-slate-50 p-3">
              <div className="flex justify-between"><span className="text-slate-500">To</span><span>{p.preview.recipient ? `${p.preview.recipient.name ?? ""} · ${p.preview.recipient.email}` : "—"}</span></div>
              {isPay && p.outstanding && (
                <div className="flex justify-between"><span className="text-slate-500">Outstanding</span><span className="font-medium">{money(p.outstanding.amount, p.outstanding.currency)}{p.outstanding.dueDate ? ` · due ${new Date(p.outstanding.dueDate).toLocaleDateString("en-GB")}` : ""}{p.outstanding.invoiceNumber ? ` · ${p.outstanding.invoiceNumber}` : ""}</span></div>
              )}
              {!isPay && (
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Chase round{p.chasesSent ? ` · ${p.chasesSent} sent so far` : ""}</span>
                  <div className="flex gap-1">
                    {[1, 2, 3].map((n) => (
                      <button key={n} type="button" onClick={() => { setRound(n); load(n); }} className={`rounded px-2.5 py-1 text-xs font-medium ${round === n ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>{n}</button>
                    ))}
                  </div>
                </div>
              )}
              {p.redirected && <p className="mt-1 text-xs text-amber-700">Outside production, sends go to you as a test copy.</p>}
            </div>
            <label className="text-xs text-slate-500">Subject<input className="mt-1 w-full rounded-md border border-slate-200 p-2 text-sm" value={subject} onChange={(e) => setSubject(e.target.value)} /></label>
            <label className="text-xs text-slate-500">Opening<textarea className="mt-1 w-full rounded-md border border-slate-200 p-2 text-sm" rows={4} value={intro} onChange={(e) => setIntro(e.target.value)} /></label>
            <label className="text-xs text-slate-500">Body<textarea className="mt-1 w-full rounded-md border border-slate-200 p-2 text-sm" rows={3} value={body} onChange={(e) => setBody(e.target.value)} />
              {template === "crew_list_reminder" && <span className="text-[11px] text-slate-400">{"{{preferences_url}}"} becomes the client's crew-list link.</span>}</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={copyFirst} onChange={(e) => setCopyFirst(e.target.checked)} />Send me a copy first</label>
          </div>
        )}
        <DialogFooter className="mt-4 flex w-full !justify-between">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" disabled={busy || !p?.canSend} onClick={() => send(true)}>Send me a preview</Button>
            <Button type="button" disabled={busy || !p?.canSend} onClick={() => (copyFirst ? send(true) : send(false))}>{copyFirst ? "Send copy to me" : "Send reminder"}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
